import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import DraftOutreachSection from 'features/IRWorkstation/components/Alerts/DraftOutreachSection';
import inputSignalsService from 'services/inputSignalsService';
import salesforceTasksService from 'services/salesforceTasksService';
import { useNotificationContext } from 'contexts/NotificationContext';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AlertDetailSection from './AlertDetailSection';
import { formatDateTime } from 'utils/utils';

const ActionDetails = ({ title, description }) => (
	<Box
		sx={{
			backgroundColor: 'vdr.bg',
			border: '0.5px solid',
			borderColor: 'vdr.border',
			borderRadius: '8px',
			padding: '12px 14px',
			mt: 1,
		}}
	>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'vdr.text',
				mb: '4px',
			}}
		>
			↔ {title}
		</Typography>

		<Typography sx={{ fontSize: '11px', color: 'vdr.text' }}>
			{description}
		</Typography>
	</Box>
);

ActionDetails.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
};

// Canonical Salesforce API names the drafting endpoint accepts. It compares them
// exactly, but an alert's salesforce_object_type is not normalized backend-side
// (for rule-derived alerts the type comes straight from Salesforce report metadata
// via the rule's `select` payload), so it arrives as 'account' or 'Account'. Match
// case-insensitively here, then always send the canonical spelling.
const DRAFT_OUTREACH_OBJECT_TYPES = {
	account: 'Account',
	aim__fundraising__c: 'AIM__Fundraising__c',
};

// The Salesforce record a draft should be based on, or null when the alert points
// at something the drafting endpoint cannot use. The endpoint resolves the account
// itself (a fundraising via its AIM__Account__c lookup), so we just forward the
// alert's own type and id.
const resolveDraftOutreachTarget = (alert) => {
	const sf = alert?.salesforce_object;
	const type =
		DRAFT_OUTREACH_OBJECT_TYPES[sf?.salesforce_object_type?.toLowerCase()];

	if (!type || !sf?.salesforce_object_id) return null;

	return { type, id: sf.salesforce_object_id };
};

const AlertsDrawer = ({
	alert,
	open,
	onClose,
	onDataChanged,
	onAlertUpdated,
	tenantId,
}) => {
	const creationDatetime = formatDateTime(alert?.event_time || Date.now());

	const draftOutreachTarget = resolveDraftOutreachTarget(alert);
	const isFailedState =
		alert?.event?.event_subcategory === 'stale_or_missing_data';
	// A failed alert has no record worth drafting from, whatever it points at.
	const canDraftOutreach = !isFailedState && !!draftOutreachTarget;

	const [updatingAcknowledgement, setUpdatingAcknowledgement] = useState(false);
	const [isCreatingTask, setIsCreatingTask] = useState(false);

	const eventContext = alert?.event?.event_context ?? {};
	const hasEventContext = Object.keys(eventContext).length > 0;
	const hasAcknowledgementInfo =
		alert?.acknowledged_at && alert?.acknowledged_by_name;

	const { showNotification } = useNotificationContext();

	const isAcknowledged = alert?.event?.event_status === 'acknowledged';

	const getAcknowledgementButtonLabel = (isAcknowledged, isUpdating) => {
		if (isUpdating) {
			return isAcknowledged ? 'Unacknowledging...' : 'Acknowledging...';
		}

		return isAcknowledged ? 'Unacknowledge' : 'Acknowledge';
	};

	const handleAcknowledge = async () => {
		const eventStatus = isAcknowledged ? 'unacknowledged' : 'acknowledged';

		try {
			setUpdatingAcknowledgement(true);

			const updatedAlert =
				await inputSignalsService.updateAcknowledgementStatus(
					alert.id,
					eventStatus
				);

			onAlertUpdated?.(updatedAlert);

			showNotification(`Alert ${eventStatus}`, 'success');
		} catch (error) {
			console.error(error);

			showNotification(
				isAcknowledged
					? 'Failed to unacknowledge alert'
					: 'Failed to acknowledge alert',
				'error'
			);
		} finally {
			setUpdatingAcknowledgement(false);
		}
	};

	const handleCreateTask = async () => {
		try {
			setIsCreatingTask(true);

			await salesforceTasksService.createTask(alert.id);
			showNotification('CRM task created successfully', 'success');

			await onDataChanged?.();

			onClose();
		} catch (error) {
			console.error('Failed to create task:', error);
			showNotification('Failed to create CRM task', 'error');
		} finally {
			setIsCreatingTask(false);
		}
	};

	return (
		<>
			{/* backdrop */}
			<Box
				data-testid='alert-backdrop'
				onClick={onClose}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>
			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 520,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						padding: '16px 18px 14px',
						borderBottom: '0.5px solid',
						borderColor: 'border.light',
						flexShrink: 0,
					}}
				>
					<Box>
						<Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
							{`${alert.event?.event_title}`}
						</Typography>

						<Typography
							sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px' }}
						>
							{`${creationDatetime} ${creationDatetime && alert.event?.event_subtitle ? ' · ' : ''} ${alert.event?.event_subtitle}`}
						</Typography>
					</Box>

					<IconButton
						data-testid='alert-drawer-close-button'
						size='small'
						onClick={onClose}
						sx={{
							width: 28,
							height: 28,
							borderRadius: '6px',
							border: '0.5px solid',
							borderColor: 'border.light',
							color: 'text.secondary',
							flexShrink: 0,
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<CloseIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Box>

				{/* Body */}
				<Box
					sx={{
						padding: '16px 18px',
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					{alert.event?.event_detail &&
						alert.event?.event_subcategory !== 'stale_or_missing_data' && (
							<Box>{alert.event?.event_detail}</Box>
						)}
					{alert.event?.event_detail &&
						alert.event?.event_subcategory === 'stale_or_missing_data' && (
							<Box
								sx={{
									fontSize: '12px',
									fontWeight: 500,
									padding: '7px 14px',
									border: '1px solid red',
									borderRadius: '6px',
									color: 'status.red.text',
									backgroundColor: 'status.red.bg',
								}}
							>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										height: '20px',
									}}
								>
									<WarningAmberOutlinedIcon
										sx={{
											fontSize: 14,
											marginTop: '7px',
										}}
									/>
									<p
										style={{
											paddingLeft: '7px',
											paddingTop: '5px',
											fontWeight: 'bold',
										}}
									>
										ALERT EXECUTION FAILED
									</p>
								</div>

								<p
									style={{
										paddingLeft: '21px',
										marginTop: '6px',
										marginBottom: '6px',
									}}
								>
									{alert.event?.event_detail}
								</p>
							</Box>
						)}

					{/* Details */}
					{(hasEventContext || hasAcknowledgementInfo) && (
						<AlertDetailSection
							event_context={eventContext}
							acknowledgedAt={alert.acknowledged_at}
							acknowledgedByName={alert.acknowledged_by_name}
						/>
					)}

					{/* Action details */}
					{alert.action?.action_title && alert.action?.action_description && (
						<ActionDetails
							title={alert.action.action_title}
							description={alert.action.action_description}
						/>
					)}

					{/* Draft Outreach */}
					{canDraftOutreach && (
						<DraftOutreachSection
							tenantId={tenantId}
							salesforceObjectType={draftOutreachTarget.type}
							salesforceObjectId={draftOutreachTarget.id}
						/>
					)}

					{/* [button] action_description */}
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							flexWrap: 'wrap',
						}}
					>
						{alert.action?.action_link && (
							<Button
								key={`alert-action-link-button`}
								href={alert.action.action_link}
								target='_blank'
								rel='noopener noreferrer'
								variant='outlined'
								size='small'
								sx={{
									fontSize: '12px',
									fontWeight: 500,
									padding: '7px 14px',
									borderRadius: '6px',
									backgroundColor: 'status.blue.bg',
									color: 'status.blue.text',
									borderColor: 'status.blue.border',
									cursor: 'pointer',
									'&:hover': { backgroundColor: 'status.blue.hover' },
								}}
							>
								{`${alert.action.action_title}` || 'Open Link'}
							</Button>
						)}

						<LoadingButton
							key={'alert-action-acknowledge-button'}
							variant='outlined'
							size='small'
							onClick={handleAcknowledge}
							loading={updatingAcknowledgement}
							sx={{
								fontSize: '12px',
								fontWeight: 500,
								padding: '7px 14px',
								borderRadius: '6px',
								borderColor: 'border.main',
								color: 'text.secondary',
								cursor: 'pointer',
							}}
						>
							{getAcknowledgementButtonLabel(
								isAcknowledged,
								updatingAcknowledgement
							)}
						</LoadingButton>

						{!isAcknowledged &&
							alert.salesforce_object?.salesforce_object_id && (
								<LoadingButton
									key={'alert-action-create-task-button'}
									variant='outlined'
									size='small'
									onClick={handleCreateTask}
									loading={isCreatingTask}
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										padding: '7px 14px',
										borderRadius: '6px',
										borderColor: 'border.main',
										color: 'text.secondary',
										cursor: 'pointer',
									}}
								>
									{isCreatingTask ? 'Creating...' : 'Create Task'}
								</LoadingButton>
							)}
					</Box>
				</Box>
			</Box>
		</>
	);
};

AlertsDrawer.propTypes = {
	alert: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onDataChanged: PropTypes.func,
	onAlertUpdated: PropTypes.func,
	tenantId: PropTypes.string,
};

export default AlertsDrawer;
